interface Range {
  min: number
  max: number
}

interface ContainsCondition {
  contains: string
  caseSensitive?: boolean
}

interface InArrayCondition<TValue = string | number | boolean> {
  arrayIncludesAnyOf?: TValue[]
  arrayIncludesAllOf?: TValue[]
  caseSensitive?: boolean
}

interface IsValueInSetCondition<TValue = string | number | boolean | null | undefined> {
  isOneOf: TValue[]
  caseSensitive?: boolean
}

type FilterValue =
  | string
  | number
  | boolean
  | Range
  | RegExp
  | ContainsCondition
  | InArrayCondition
  | IsValueInSetCondition
  | undefined

export interface FilterCriteria {
  [attribute: string]: FilterValue
}

type DataObject = Record<string, unknown>

const PATH_NOT_FOUND = Symbol('pathNotFound')

/**
 * The function `getValueByPathParts` retrieves a value from a nested object based
 * on an array of path parts.
 * @param {DataObject} obj - The `obj` parameter in the `getValueByPathParts`
 * function is a `DataObject`, which is an object that contains the data you want
 * to access using the path specified in the `pathParts` array.
 * @param {string[]} pathParts - PathParts is an array of strings representing the
 * sequence of keys to traverse in the object to retrieve the desired value.
 * @returns The function `getValueByPathParts` returns either the value found at
 * the specified path in the `obj` parameter or it returns the constant
 * `PATH_NOT_FOUND` if the path does not exist in the object.
 */
function getValueByPathParts(
  obj: DataObject,
  pathParts: string[]
): unknown | typeof PATH_NOT_FOUND {
  let current: unknown = obj
  for (const part of pathParts) {
    if (
      typeof current === 'object' &&
      current !== null &&
      Object.prototype.hasOwnProperty.call(current, part)
    ) {
      current = (current as Record<string, unknown>)[part]
    } else {
      return PATH_NOT_FOUND
    }
  }
  return current
}

/**
 * Type guard to check if a value is a Range object.
 * @param value The value to check (of unknown type).
 * @returns True if the value is a valid Range object, false otherwise.
 */
function isRange(value: unknown): value is Range {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  // Value is a non-null object. Check for 'min' and 'max' properties and their types.
  // We use hasOwnProperty to ensure these are own properties.
  const hasMin = Object.prototype.hasOwnProperty.call(value, 'min')
  const hasMax = Object.prototype.hasOwnProperty.call(value, 'max')

  if (!hasMin || !hasMax) {
    return false
  }

  // Assert 'value' to temporarily treat it as having 'min' and 'max' properties
  // of type 'unknown' to check their actual types.
  return (
    typeof (value as { min: unknown }).min === 'number' &&
    typeof (value as { max: unknown }).max === 'number'
  )
}

/**
 * The function `isInArrayCondition` checks if a value meets the condition of being
 * an object with either `arrayIncludesAnyOf` or `arrayIncludesAllOf` properties
 * that are arrays.
 * @param {unknown} value - The `value` parameter in the `isInArrayCondition`
 * function is of type `unknown`, which means it can be any type. The function
 * checks if the `value` is an object (excluding `null`) and then further checks if
 * it has either an `arrayIncludesAnyOf` or
 * @returns The function `isInArrayCondition` is returning a boolean value. It
 * returns `true` if the `value` parameter meets the condition of being an object
 * with either `arrayIncludesAnyOf` or `arrayIncludesAllOf` properties that are
 * arrays. Otherwise, it returns `false`.
 */
function isInArrayCondition(value: unknown): value is InArrayCondition {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const v = value as InArrayCondition
  return Array.isArray(v.arrayIncludesAnyOf) || Array.isArray(v.arrayIncludesAllOf)
}

/**
 * The function `isValueInSetCondition` checks if a given value is of type
 * `IsValueInSetCondition` by verifying if it is an object with an array property
 * named `isOneOf`.
 * @param {unknown} value - The `value` parameter in the `isValueInSetCondition`
 * function is of type `unknown`, which means it can be any type. The function
 * checks if the `value` is an object and not null, then it casts it to type
 * `IsValueInSetCondition` and checks if
 * @returns The function `isValueInSetCondition` is returning a boolean value. It
 * returns `true` if the `value` parameter is an object with a property `isOneOf`
 * that is an array, and `false` otherwise.
 */
function isValueInSetCondition(value: unknown): value is IsValueInSetCondition {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const v = value as IsValueInSetCondition
  return Array.isArray(v.isOneOf)
}

// Helper to normalize a value for comparison (handles case for strings)
const normalizeValue = (
  val: unknown,
  isCaseSensitive: boolean
): string | number | boolean | null | undefined => {
  if (typeof val === 'string') {
    return isCaseSensitive ? val : val.toLowerCase()
  }
  if (typeof val === 'number' || typeof val === 'boolean' || val === null || val === undefined) {
    return val
  }
  // If val is an object/array itself within itemValue, this simple normalization won't work well for Set comparison.
  // This filter is designed for itemValue being an array of primitives.
  return undefined // Fallback to string conversion, might not be ideal for all complex types in arrays.
}

/**
 * The `filter` function in TypeScript filters an array of items based on specified
 * criteria, supporting various filter types like direct comparison, string
 * contains, regex match, range check, and more.
 * @param {T[]} items - The `items` parameter in the `filter` function is an array
 * of objects of type `T`, where `T` extends `DataObject`. This array contains the
 * items that need to be filtered based on the provided criteria.
 * @param {FilterCriteria} criteria - The `criteria` parameter in the `filter`
 * function is an object that defines the filtering conditions for the items in the
 * array. Each key in the `criteria` object represents a path to a property within
 * the items, and the corresponding value defines the condition that property must
 * meet for the item to be
 * @param [logger] - The `logger` parameter in the `filter` function is an optional
 * callback function that can be used to log messages during the filtering process.
 * If provided, the `logger` function will be called with a message as its argument
 * to log information or errors encountered while filtering the items based on the
 * criteria.
 * @returns The `filter` function returns an array of items that match the
 * specified criteria based on the filtering logic implemented in the function.
 */
export function filter<T extends DataObject>(
  items: T[],
  criteria: FilterCriteria,
  logger?: (message: unknown) => void
): T[] {
  if (!items || items.length === 0) {
    return []
  }

  const criteriaEntries = Object.entries(criteria) // Get [path, condition] pairs
  if (criteriaEntries.length === 0) {
    return [...items]
  }
  // Pre-process criteria: split paths and store them and filter out empty conditions (undefined or empty string)
  const processedCriteria = criteriaEntries
    .map(([path, condition]) => ({
      pathParts: path.split('.'), // Split paths ONCE here
      condition,
      originalPath: path, // Keep for logging if needed
    }))
    .filter(({ condition }) => condition !== undefined && condition !== '') // Filter out empty conditions
  return items.filter((item: T) => {
    // Ignore null items from list initialisation
    if (item === null || item === undefined) {
      return true
    }

    for (const pc of processedCriteria) {
      const itemValueResult = getValueByPathParts(item, pc.pathParts)
      const condition = pc.condition

      if (itemValueResult === PATH_NOT_FOUND) {
        if (logger)
          logger(
            `Path "${pc.originalPath}" not found on item. Skipping this criterion for this item.`
          )
        continue
      }

      const itemValue: unknown = itemValueResult

      if (
        typeof condition === 'string' ||
        typeof condition === 'number' ||
        typeof condition === 'boolean'
      ) {
        // Direct comparison for primitives
        if (itemValue !== condition) {
          return false
        }
      } else if (typeof condition === 'object' && 'contains' in condition) {
        if (typeof condition.contains !== 'string') {
          if (logger)
            logger(
              `Filter error: 'contains' value in condition for path '${pc.originalPath}' is not a string.`
            )
          return false // Cannot perform 'contains' if the search term isn't a string.
        }

        const isCaseSensitive = condition.caseSensitive === true
        const rawSearchTerm = condition.contains

        if (typeof itemValue === 'string') {
          const searchValue = isCaseSensitive ? itemValue : itemValue.toLowerCase()
          const searchTerm = isCaseSensitive ? rawSearchTerm : rawSearchTerm.toLowerCase()
          // String contains check
          if (!searchValue.includes(searchTerm)) {
            return false
          }
        } else if (Array.isArray(itemValue)) {
          const searchTerm = condition.contains.toLowerCase()
          const foundInArray = itemValue.some((item) => {
            if (item === null || item === undefined) {
              return false // Skip null or undefined values
            }
            const stringItem = String(item) // Convert to string for comparison
            const searchItem = isCaseSensitive ? stringItem : stringItem.toLowerCase()
            return searchItem.includes(searchTerm)
          })
          if (!foundInArray) {
            return false
          }
        } else {
          if (logger)
            logger(
              `Value for 'contains' check at path '${pc.originalPath}' is not a string or array; it's type: ${typeof itemValue}.`
            )
          return false // Not a string or array, can't check contains
        }
      } else if (condition instanceof RegExp) {
        if (typeof itemValue === 'string') {
          // Regular expression match
          if (!condition.test(itemValue)) {
            return false
          }
        } else {
          return false // Not a string, can't match regex
        }
      } else if (isRange(condition)) {
        if (typeof itemValue === 'number') {
          // Range check
          if (itemValue < condition.min || itemValue > condition.max) {
            return false
          }
        } else {
          return false // Not a number, can't check range
        }
      } else if (isValueInSetCondition(condition)) {
        const isCaseSensitive = condition.caseSensitive === true

        const normalizedItemValue = normalizeValue(itemValue, isCaseSensitive)

        if (normalizedItemValue === undefined && itemValue !== undefined) {
          if (logger)
            logger(
              `Item value at path '${pc.originalPath}' (type: ${typeof itemValue}) is not a comparable primitive for 'isOneOf' check.`
            )
          return false // This condition type only works on primitive itemValues.
        }

        if (!Array.isArray(condition.isOneOf) || condition.isOneOf.length === 0) {
          if (logger)
            logger(
              `'isOneOf' array in condition for path '${pc.originalPath}' is empty or not an array. Cannot match.`
            )
          return false // itemValue cannot be in an empty/invalid set of choices.
        }

        let matchFound = false
        for (const targetValue of condition.isOneOf) {
          const normalizedTargetValue = normalizeValue(targetValue, isCaseSensitive)

          // If the target itself was an incompatible type (e.g. an object within isOneOf list),
          // normalizedTargetValue would be undefined (unless targetValue was literally undefined).
          // We only proceed if the target is valid.
          if (normalizedTargetValue === undefined && targetValue !== undefined) {
            if (logger)
              logger(
                `Skipping incompatible target value in 'isOneOf' list for path '${pc.originalPath}'. Target: ${String(targetValue)}`
              )
            continue
          }

          if (normalizedItemValue === normalizedTargetValue) {
            matchFound = true
            break // Found a match, no need to check further
          }
        }

        if (!matchFound) {
          return false // itemValue was not found in the 'isOneOf' list.
        }
      } else if (isInArrayCondition(condition)) {
        if (!Array.isArray(itemValue)) {
          if (logger)
            logger(
              `Item value ${itemValue} at path '${pc.originalPath}' is not an array for arrayInclusion check.`
            )
          return false // Not an array, can't check array conditions
        }

        const isCaseSensitive = condition.caseSensitive === true

        const itemSet = new Set(
          itemValue
            .filter(
              (v) =>
                typeof v === 'string' ||
                typeof v === 'number' ||
                typeof v === 'boolean' ||
                v === null ||
                v === undefined
            )
            .map((v) => normalizeValue(v, isCaseSensitive))
        )

        if (Array.isArray(condition.arrayIncludesAllOf)) {
          const targets = condition.arrayIncludesAllOf
          if (targets.length > 0) {
            // Only check if there are specific targets to find all of.
            const allTargetsFound = targets.every((target) => {
              // Ensure target is a primitive before normalizing and checking
              if (
                typeof target === 'string' ||
                typeof target === 'number' ||
                typeof target === 'boolean' ||
                target === null ||
                target === undefined
              ) {
                return itemSet.has(normalizeValue(target, isCaseSensitive))
              }
              return false // Non-primitive target cannot be in itemSet of primitives.
            })
            if (!allTargetsFound) {
              return false // Fails if not all targets are found
            }
          }
        }

        if (Array.isArray(condition.arrayIncludesAnyOf)) {
          const targets = condition.arrayIncludesAnyOf
          if (targets.length === 0) {
            // If looking for "any of an empty set", no match can be found.
            return false
          }
          const anyTargetFound = targets.some((target) => {
            // Ensure target is a primitive
            if (
              typeof target === 'string' ||
              typeof target === 'number' ||
              typeof target === 'boolean' ||
              target === null ||
              target === undefined
            ) {
              return itemSet.has(normalizeValue(target, isCaseSensitive))
            }
            return false
          })
          if (!anyTargetFound) {
            return false // Fails if none of the targets are found
          }
        }
      } else if (condition === undefined) {
        if (itemValue !== undefined) {
          return false // Not undefined, but condition is undefined
        }
      } else {
        // Handle other potential filter types if needed
        if (logger) logger(`Unsupported filter type for path: ${pc.originalPath}`)
        return false
      }
    }
    return true
  })
}
